import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import imageCompression from 'browser-image-compression';
import pako from 'pako';
import { motion, AnimatePresence } from 'framer-motion';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import transitionGif from './assets/transition.gif';
import { playSound } from './utils/sounds';
import sndSwitch from './sound/10_UI_Menu_SFX/092_Pause_04.wav';

// ---=== Impor Suara Baru yang Lebih Keren! ===---
import sndFileSelect from './sound/10_UI_Menu_SFX/013_Confirm_03.wav';
import sndOptionClick from './sound/10_UI_Menu_SFX/001_Hover_01.wav';
import sndCompress from './sound/10_UI_Menu_SFX/070_Equip_10.wav';
import sndShare from './sound/10_UI_Menu_SFX/079_Buy_sell_01.wav';
import sndSuccess from './sound/SoupTonic UI1 SFX Pack 1 - mp3/SFX_UI_Saved.mp3';
import sndError from './sound/10_UI_Menu_SFX/033_Denied_03.wav';
import sndIntro from './sound/8_Buffs_Heals_SFX/30_Revive_03.wav';
// import backSound from './sound/ahh.mp3';

import IntroScreen from './IntroScreen';
import ImageConverter from './components/Converter/ImageConverter.js';
import FileConverter from './components/FileConverter/FileConverter.js';

// ---=== Fungsi Helper yang kembali dari petualangan ===---
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${['Bytes','KB','MB','GB'][i]}`;
};

// ---=== Komponen Anak ===---
const ImagePreview = ({ originalUrl, compressedUrl }) => (
  <div className="preview-container">
    <div className="preview-item">
      <h4 className="preview-label">Asli</h4>
      <img src={originalUrl} alt="Gambar Asli" />
    </div>
    <div className="preview-item">
      <h4 className="preview-label">Hasil Kompresi</h4>
      <img src={compressedUrl} alt="Gambar Hasil Kompresi" />
    </div>
  </div>
);

const ResultCard = ({ results, onReset, showSharePopup, setShowSharePopup, setPopupPosition, popupPosition }) => {
  if (!results || results.length === 0) return null;

  const totalOriginalSize = results.reduce((acc, r) => acc + r.originalSize, 0);
  const totalCompressedSize = results.reduce((acc, r) => acc + r.compressedSize, 0);
  const totalRatio = ((1 - totalCompressedSize / totalOriginalSize) * 100).toFixed(2);

  const handleShare = (event) => {
    const textToCopy = `Aku baru saja menghemat ${formatBytes(totalOriginalSize - totalCompressedSize)} dengan Kompresor Ajaib! ✨ Coba juga di https://kompresi-pwa.vercel.app/`;
    navigator.clipboard.writeText(textToCopy);
    playSound(sndShare);
    setPopupPosition({ top: event.clientY, left: event.clientX });
    setShowSharePopup(true);
    setTimeout(() => setShowSharePopup(false), 2500);
  };
  
  const downloadAllAsZip = async () => {
    playSound(sndShare);
    const zip = new JSZip();
    for (const result of results) {
      const response = await fetch(result.url);
      const blob = await response.blob();
      zip.file(`compressed-${result.name}`, blob);
    }
    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, 'KompresorAjaib_Hasil.zip');
    });
  };

  return (
    <>
      <div className="result-section">
        <h3>Sihir Selesai!</h3>
        {results.length > 1 && (
          <div className="result-summary">
            <div className="result-item">
              <div className="result-label">Total Hemat</div>
              <div className="result-value">{formatBytes(totalOriginalSize - totalCompressedSize)}</div>
            </div>
            <div className="result-item">
              <div className="result-label">Rasio Rata-rata</div>
              <div className="result-value">{totalRatio}%</div>
            </div>
          </div>
        )}

        <ul className="result-list">
          {results.map((result, index) => (
            <li key={index} className="result-list-item">
              <div className="result-file-info">
                <p className="result-file-name">{result.name}</p>
                <p className="result-size-change">
                  {formatBytes(result.originalSize)} → <strong>{formatBytes(result.compressedSize)}</strong>
                </p>
              </div>
              {result.skipped ? (
                  <div className="result-stats skipped">
                      <p className="result-ratio-label">{result.message}</p>
                  </div>
              ) : (
                  <div className="result-stats">
                      <p className="result-ratio-value">{result.compressionRatio}%</p>
                      <p className="result-ratio-label">Hemat</p>
                  </div>
              )}
              <a href={result.url} download={`compressed-${result.name}`} className="action-button download-button">
                Unduh
              </a>
            </li>
          ))}
        </ul>

        {results.length > 0 && results[0].type.startsWith('image/') && results.length === 1 && (
          <ImagePreview originalUrl={results[0].originalUrl} compressedUrl={results[0].url} />
        )}
      </div>
      
      {results.length > 1 && (
        <button onClick={downloadAllAsZip} className="action-button">
          Unduh Semua (.zip)
        </button>
      )}
      <button onClick={onReset} className="action-button secondary">
        Kompres Lagi!
      </button>
      <button onClick={handleShare} className="action-button share-button">
        Pamerkan Hasil!
      </button>

      <AnimatePresence>
        {showSharePopup && (
          <motion.div 
            className="share-popup"
            style={{ top: popupPosition.top, left: popupPosition.left }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
          >
            Teks berhasil disalin ke clipboard!
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const InstallButton = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Deteksi OS
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isDeviceIos = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    setIsIos(isDeviceIos);

    const isDeviceAndroid = /android/i.test(userAgent);
    setIsAndroid(isDeviceAndroid);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = () => {
    if (isIos) {
      setShowIosInstructions(true);
      return;
    }
    if (installPrompt) {
      installPrompt.prompt();
    }
  };

  const canInstall = installPrompt || isIos;

  if (!canInstall) {
    return null; // Jangan tampilkan tombol jika tidak bisa diinstal
  }

  const getButtonContent = () => {
    let icon = '↓';
    let text = 'Instal Aplikasi';

    if (isIos) {
      icon = '';
      text = 'Instal untuk iOS';
    } else if (isAndroid || installPrompt) {
      // installPrompt akan muncul di Chrome (Android) dan browser desktop yang mendukung
      icon = '⍝'; // Simbol Android yang sudah ada
      text = 'Instal untuk Android';
    }
    
    // Untuk desktop yang mendukung PWA tapi bukan Android
    if (!isAndroid && !isIos && installPrompt){
      text = 'Instal Aplikasi';
    }

    return (
      <>
        <span className="install-icon">{icon}</span>
        {text}
      </>
    );
  };

  return (
    <>
      <button className="install-button" onClick={handleInstallClick}>
        {getButtonContent()}
      </button>
      {showIosInstructions && (
        <div className="ios-instructions-overlay" onClick={() => setShowIosInstructions(false)}>
          <div className="ios-instructions">
            <h3>Instal di iPhone/iPad</h3>
            <p>Untuk menginstal, ketuk tombol "Bagikan" di browser Safari, lalu pilih "Tambahkan ke Layar Utama".</p>
            <button onClick={() => setShowIosInstructions(false)}>Mengerti</button>
          </div>
        </div>
      )}
    </>
  );
};

// ---=== Komponen Halaman Kompresor ===---
const CompressorPage = ({ showIntro, handleIntroFinish, setIsTransitioning }) => {
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState(null);
  const [compressionMode, setCompressionMode] = useState('recipe'); // 'recipe' or 'slider'
  const [recipe, setRecipe] = useState('seimbang'); 
  const [smartTarget, setSmartTarget] = useState('sedang'); // 'kecil', 'sedang', 'tinggi'
  const [compressionTarget, setCompressionTarget] = useState(80);
  const [view, setView] = useState('uploader');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;
    playSound(sndFileSelect);
    setError(null);
    setFiles(selectedFiles);
  };
  
  const handleReset = () => {
    playSound(sndOptionClick);
    setFiles([]);
    setResults([]);
    setError(null);
    setView('uploader');
  };

  const getCompressionOptions = (setting) => {
    let imageOptions;
  
    if (typeof setting === 'string') {
      // Mode Resep
      switch (setting) {
        case 'kilat':
          imageOptions = { maxSizeMB: 1, initialQuality: 0.8 };
          break;
        case 'kualitas':
          imageOptions = { maxSizeMB: 0.2, initialQuality: 0.95 };
          break;
        default: // seimbang
          imageOptions = { maxSizeMB: 0.5, initialQuality: 0.9 };
          break;
      }
    } else {
      // Mode Slider, 'setting' adalah persentase kualitas
      const quality = setting / 100;
      imageOptions = {
        maxSizeMB: 2 - (quality * 1.8), // Semakin tinggi kualitas, semakin besar ukuran file
        initialQuality: quality,
      };
    }
    
    return { ...imageOptions, useWebWorker: true };
  };

  const smartCompress = async (file, target) => {
    let targetSizeKB;
    switch (target) {
        case 'kecil': targetSizeKB = 200; break;
        case 'tinggi': targetSizeKB = 1000; break;
        default: targetSizeKB = 500; break; // sedang
    }

    // Coba kompres dengan pengaturan awal yang cukup tinggi
    let options = {
        maxSizeMB: targetSizeKB / 1024,
        initialQuality: 0.9,
        useWebWorker: true,
    };
    let compressedFile = await imageCompression(file, options);

    // Jika hasilnya terlalu besar, coba kurangi kualitas secara bertahap
    // Ini adalah pendekatan heuristik sederhana (mirip "AI")
    while (compressedFile.size > targetSizeKB * 1024 && options.initialQuality > 0.1) {
        options.initialQuality = Math.max(0.1, options.initialQuality - 0.1); // pastikan tidak negatif
        // Turunkan maxSizeMB sedikit di bawah ukuran terakhir untuk memberi ruang
        options.maxSizeMB = (compressedFile.size / 1024 / 1024) * 0.95; 
        
        try {
            compressedFile = await imageCompression(file, options);
        } catch (error) {
            console.error("Gagal saat iterasi kompresi cerdas:", error);
            // Jika gagal di tengah jalan, kembalikan hasil sukses terakhir
            break; 
        }
    }

    return compressedFile;
  }

  const compressFiles = useCallback(async () => {
    if (files.length === 0) return;
    setIsCompressing(true);
    setError(null);
    playSound(sndCompress);
    setProgress({ current: 0, total: files.length });

    const newResults = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress({ current: i + 1, total: files.length });
      
      try {
        if (file.type.startsWith('image/')) {
          let compressedFile;
          // Logika baru untuk kompresi cerdas
          if (recipe === 'cerdas') {
            compressedFile = await smartCompress(file, smartTarget);
          } else {
            const options = getCompressionOptions(compressionMode === 'recipe' ? recipe : compressionTarget);
            compressedFile = await imageCompression(file, options);
          }
          const originalUrl = URL.createObjectURL(file);
          const compressedUrl = URL.createObjectURL(compressedFile);
          newResults.push({
            name: file.name,
            originalSize: file.size,
            compressedSize: compressedFile.size,
            compressionRatio: ((1 - compressedFile.size / file.size) * 100).toFixed(2),
            url: compressedUrl,
            originalUrl: originalUrl,
            type: file.type
          });
        } else if (file.name.endsWith('.docx') || file.name.endsWith('.pdf')) {
            // Lewati kompresi untuk format yang sudah dikompresi
            const fileUrl = URL.createObjectURL(file);
            newResults.push({
                name: file.name,
                originalSize: file.size,
                compressedSize: file.size, // Ukuran tidak berubah
                compressionRatio: 0,
                url: fileUrl,
                originalUrl: fileUrl,
                type: file.type,
                skipped: true, // Tambahkan flag untuk UI
                message: 'Format ini sudah terkompresi.'
            });
        } else {
          // Kompresi file lain (non-gambar) dengan pako
          const fileBuffer = await file.arrayBuffer();
          const compressedData = pako.gzip(fileBuffer);
          const compressedBlob = new Blob([compressedData]);
          const fileUrl = URL.createObjectURL(compressedBlob);
          newResults.push({
            name: `${file.name}.gz`,
            originalSize: file.size,
            compressedSize: compressedBlob.size,
            compressionRatio: ((1 - compressedBlob.size / file.size) * 100).toFixed(2),
            url: fileUrl,
            type: 'application/gzip'
          });
        }
      } catch (err) {
        setError(`Gagal memproses ${file.name}: ${err.message}`);
        playSound(sndError);
      }
    }
    
    playSound(sndSuccess);
    setResults(newResults);
    setView('result');
    setIsCompressing(false);
  }, [files, compressionMode, recipe, compressionTarget, smartTarget]);

  const renderUploader = () => (
    <>
      <div className="upload-section">
        {files.length === 0 ? (
          <label className="file-input-label">
            <input type="file" multiple onChange={handleFileChange} className="file-input" />
            <span className="file-input-text">Taruh File di Sini!</span>
          </label>
        ) : (
          <>
            <ul className="file-list">
              {files.map((f, i) => <li key={i}><span className="file-name">{f.name}</span><span className="file-size">({formatBytes(f.size)})</span></li>)}
            </ul>
            <label className="file-input-label secondary">
              <input type="file" multiple onChange={handleFileChange} className="file-input" />
              <span>Ganti/Tambah File</span>
            </label>
          </>
        )}
      </div>

      {files.length > 0 && (
        <motion.div className="controls-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="control-mode-switcher">
            <button className={compressionMode === 'recipe' ? 'active' : ''} onClick={() => { setCompressionMode('recipe'); playSound(sndOptionClick); }}>Resep</button>
            <button className={compressionMode === 'slider' ? 'active' : ''} onClick={() => { setCompressionMode('slider'); playSound(sndOptionClick); }}>Slider</button>
          </div>
          
          <AnimatePresence mode="wait">
            {compressionMode === 'recipe' ? (
              <motion.div key="recipe" {...cardVariants} className="recipe-container">
                <div className="recipe-buttons">
                  <button className={`recipe-button ${recipe === 'kilat' ? 'active' : ''}`} onClick={() => { setRecipe('kilat'); playSound(sndOptionClick, 0.2); }}>Kilat</button>
                  <button className={`recipe-button ${recipe === 'seimbang' ? 'active' : ''}`} onClick={() => { setRecipe('seimbang'); playSound(sndOptionClick, 0.2); }}>Seimbang</button>
                  <button className={`recipe-button ${recipe === 'kualitas' ? 'active' : ''}`} onClick={() => { setRecipe('kualitas'); playSound(sndOptionClick, 0.2); }}>Kualitas</button>
                  <button className={`recipe-button ${recipe === 'cerdas' ? 'active' : ''}`} onClick={() => { setRecipe('cerdas'); playSound(sndOptionClick, 0.2); }}>Cerdas</button>
                </div>
                
                <AnimatePresence>
                {recipe === 'cerdas' && (
                  <motion.div key="smart-options" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="smart-options-container">
                      <p className="recipe-label">Target Penggunaan:</p>
                      <div className="recipe-buttons">
                        <button className={`recipe-button small ${smartTarget === 'kecil' ? 'active' : ''}`} onClick={() => { setSmartTarget('kecil'); playSound(sndOptionClick, 0.2); }}>Email (Kecil)</button>
                        <button className={`recipe-button small ${smartTarget === 'sedang' ? 'active' : ''}`} onClick={() => { setSmartTarget('sedang'); playSound(sndOptionClick, 0.2); }}>Web (Sedang)</button>
                        <button className={`recipe-button small ${smartTarget === 'tinggi' ? 'active' : ''}`} onClick={() => { setSmartTarget('tinggi'); playSound(sndOptionClick, 0.2); }}>Arsip (Besar)</button>
                      </div>
                  </motion.div>
                )}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div key="slider" {...cardVariants} className="slider-container">
                <label htmlFor="compression-slider">Target Kompresi</label>
                <input id="compression-slider" type="range" min="10" max="99" value={compressionTarget}
                       onChange={(e) => {
                         setCompressionTarget(parseInt(e.target.value));
                         playSound(sndOptionClick, 0.2);
                       }}
                       className="target-slider" />
                <div className="target-slider-value">{compressionTarget}%</div>
              </motion.div>
            )}
          </AnimatePresence>
          <button onClick={compressFiles} className="action-button">
            Sikaaat {files.length > 1 ? `(${files.length} File)` : ''}!
          </button>
        </motion.div>
      )}
    </>
  );

  useEffect(() => {
    if(setIsTransitioning) setIsTransitioning(false);
  }, [setIsTransitioning]);

  return (
    <div className="compressor-page-container">
    <AnimatePresence>
        {showIntro && <IntroScreen onFinish={handleIntroFinish} />}
    </AnimatePresence>

    <AnimatePresence>
        {isCompressing && (
        <motion.div className="loading-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <img src="https://i.pinimg.com/originals/4f/4e/16/4f4e1638e028090ff030ec2ae0fc6919.gif" alt="animasi kompresi" className="loading-animation"/>
            <p className="progress-text">{`Memproses file ${progress.current} dari ${progress.total}...`}</p>
        </motion.div>
        )}
    </AnimatePresence>
    
    <header className="App-header">
        <h1>Kompresor Ajaib</h1>
        <p className="subtitle">Ubah file besarmu jadi kecil, bisa borongan!</p>
    </header>
    
    <main className="main-card">
        <AnimatePresence mode="wait">
        <motion.div key={view} {...cardVariants}>
            {view === 'uploader' ? renderUploader() : <ResultCard 
                results={results} 
                onReset={handleReset} 
                showSharePopup={showSharePopup} 
                setShowSharePopup={setShowSharePopup}
                popupPosition={popupPosition}
                setPopupPosition={setPopupPosition} 
            />}
        </motion.div>
        </AnimatePresence>
        
        {error && (
        <motion.div className="message-box error" {...cardVariants}>
            <p>{error}</p>
        </motion.div>
        )}

        {view === 'uploader' && <div className="install-button-container"><InstallButton /></div>}
    </main>
    </div>
  )
}

// ---=== Komponen Utama (Router) ===---
function App() {
  const [showIntro, setShowIntro] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hasSeenIntro = sessionStorage.getItem('hasSeenIntro');
    if (!hasSeenIntro) {
      setShowIntro(true);
    } else {
      // Jika intro sudah pernah dilihat, coba putar musik
      // audioRef.current?.play().catch(() => {});
    }
  }, []);

  const handleIntroFinish = () => {
    setShowIntro(false);
    sessionStorage.setItem('hasSeenIntro', 'true');
    // Coba putar musik setelah intro selesai
    // audioRef.current?.play().catch(() => {});
  };

  const handleNavigate = (path) => {
    if (location.pathname === path) return; // Jangan navigasi ke halaman yang sama
    playSound(sndSwitch, 0.6);
    setIsTransitioning(true);
    setTimeout(() => {
      navigate(path);
    }, 700);
  };

  return (
    <div className="App">
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            className="page-transition-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <img src={transitionGif} alt="Animasi transisi" className="page-transition-gif" />
          </motion.div>
        )}
      </AnimatePresence>

      <main className="router-view-container">
        <Routes>
          <Route path="/" element={<CompressorPage showIntro={showIntro} handleIntroFinish={handleIntroFinish} setIsTransitioning={setIsTransitioning} />} />
          <Route path="/converter" element={<ImageConverter setIsTransitioning={setIsTransitioning} />} />
          <Route path="/file-converter" element={<FileConverter setIsTransitioning={setIsTransitioning} />} />
        </Routes>
      </main>

      <footer className="App-footer">
        <nav>
            <div className="nav-switch">
                <div className={`nav-switch-slider ${location.pathname === '/converter' ? 'converter' : ''}`}></div>
                <button className={`nav-switch-option ${location.pathname === '/' ? 'active' : ''}`} onClick={() => handleNavigate('/')}>
                    Kompresor
                </button>
                <button className={`nav-switch-option ${location.pathname === '/converter' ? 'active' : ''}`} onClick={() => handleNavigate('/converter')}>
                    Konverter
                </button>
            </div>
        </nav>
        {location.pathname === '/' && <p>Dibuat oleh seseorang yang capek hidup dengan bantuan jin</p>}
      </footer>
      
      {/* <audio ref={audioRef} src={backSound} loop volume={0.05} /> */}
    </div>
  );
}

const cardVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

export default App;